import "./index.css";
import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Auth from "./pages/Auth";
import PortalSelection from "./pages/PortalSelection";
import NotFound from "./pages/NotFound";
import StudentDashboard from "./pages/student/StudentDashboard";
import StudentRegistration from "./pages/student/StudentRegistration";
import StudentRegistrationSuccess from "./pages/student/StudentRegistrationSuccess";
import HostelAdminDashboard from "./pages/HostelAdminDashboard";

import HostelOnboarding from "./pages/HostelOnboarding";
import InstitutionOnboarding from "./pages/InstitutionOnboarding";
import InstitutionAdminDashboard from "./pages/InstitutionAdminDashboard";
import DonorDashboard from "./pages/DonorDashboard";
import TrusteeDashboard from "./pages/TrusteeDashboard";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import ImpactReports from "./pages/donor/ImpactReports";
import TaxReceipts from "./pages/donor/TaxReceipts";
import VolunteerSign from "./pages/donor/VolunteerSign";
import TrusteeInstitutions from "./pages/trustee/Institutions";
import TrusteeScholarships from "./pages/trustee/Scholarships";
import TrusteeLoans from "./pages/trustee/Loans";
import TrusteeNutrition from "./pages/trustee/Nutrition";
import TrusteeResources from "./pages/trustee/Resources";
import TrusteeHelp from "./pages/trustee/Help";
import HostelHostels from "./pages/hostel/Hostels";
import HostelScholarships from "./pages/hostel/Scholarships";
import HostelFunding from "./pages/hostel/Funding";
import HostelReports from "./pages/hostel/Reports";
import HostelCMS from "./pages/hostel/CMS";
import HostelCRM from "./pages/hostel/CRM";
import HostelSettings from "./pages/hostel/Settings";

const queryClient = new QueryClient();

const PrefixedRoutes = () => {
  return (
    <Routes>
      {/* Redirect root to portal selection */}
      <Route path="/" element={<Navigate to="/" replace />} />
      <Route path="/portal-selection" element={<PortalSelection />} />
      <Route path="/auth" element={<Auth />} />

      {/* Hostel Registration Flow */}
      <Route
        path="/hostel/register"
        element={<Navigate to="/auth?portal=hostel" replace />}
      />
      <Route path="/hostel/onboarding" element={<HostelOnboarding />} />

      {/* Institution Registration Flow */}
      <Route
        path="/institution/register"
        element={<Navigate to="/auth?portal=institution" replace />}
      />
      <Route
        path="/institution/onboarding"
        element={<InstitutionOnboarding />}
      />

      {/* Student Portal */}
      <Route
        path="/student/dashboard"
        element={
          <ProtectedRoute>
            <StudentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/student"
        element={
          <ProtectedRoute>
            <StudentDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/student/register" element={<StudentRegistration />} />
      <Route
        path="/student/registration-success"
        element={<StudentRegistrationSuccess />}
      />

      {/* Hostel Admin Portal */}
      <Route
        path="/dashboard/hostel-admin"
        element={
          <ProtectedRoute>
            <HostelAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hostels"
        element={
          <ProtectedRoute>
            <HostelHostels />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scholarships"
        element={
          <ProtectedRoute>
            <HostelScholarships />
          </ProtectedRoute>
        }
      />
      <Route
        path="/funding"
        element={
          <ProtectedRoute>
            <HostelFunding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <HostelReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cms"
        element={
          <ProtectedRoute>
            <HostelCMS />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm"
        element={
          <ProtectedRoute>
            <HostelCRM />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <HostelSettings />
          </ProtectedRoute>
        }
      />

      {/* Institution Admin Portal */}
      <Route
        path="/dashboard/institution-admin"
        element={
          <ProtectedRoute>
            <InstitutionAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/institution-admin/*"
        element={
          <ProtectedRoute>
            <InstitutionAdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Donor Portal */}
      <Route
        path="/dashboard/donor"
        element={
          <ProtectedRoute>
            <DonorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/donor/impact-reports"
        element={
          <ProtectedRoute>
            <ImpactReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/donor/tax-receipts"
        element={
          <ProtectedRoute>
            <TaxReceipts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/donor/volunteer"
        element={
          <ProtectedRoute>
            <VolunteerSign />
          </ProtectedRoute>
        }
      />

      {/* Trustee Portal */}
      <Route
        path="/dashboard/trustee"
        element={
          <ProtectedRoute>
            <TrusteeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trustee/institutions"
        element={
          <ProtectedRoute>
            <TrusteeInstitutions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trustee/scholarships"
        element={
          <ProtectedRoute>
            <TrusteeScholarships />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trustee/loans"
        element={
          <ProtectedRoute>
            <TrusteeLoans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trustee/nutrition"
        element={
          <ProtectedRoute>
            <TrusteeNutrition />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trustee/resources"
        element={
          <ProtectedRoute>
            <TrusteeResources />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trustee/help"
        element={
          <ProtectedRoute>
            <TrusteeHelp />
          </ProtectedRoute>
        }
      />

      {/* Super Admin Portal */}
      <Route
        path="/dashboard/super-admin"
        element={
          <ProtectedRoute>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-admin/*"
        element={
          <ProtectedRoute>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Application root component with AuthProvider wrapping all routes
const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <PrefixedRoutes />
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
